import React, {
  useEffect, useRef, useState, useMemo,
} from 'react';
import { useOfficialHomeContext } from '@/components/overseaHome/OfficialHomeContext';
import { OFFICIAL_HOME_LOCALIZED_CONTENT } from '@/components/overseaHome/localizedContent';
import DeepSeekSvg from './assets/DeepSeek.svg';
import DoubaoSvg from './assets/Doubao.svg';
import ElevenLabsSvg from './assets/ElevenLabs.svg';
import FluxSvg from './assets/Flux.svg';
import GptImageSvg from './assets/GPT-Image.svg';
import GeminiSvg from './assets/GeminiIcon.svg';
import GrokSvg from './assets/Grok.svg';
import HunyuanSvg from './assets/Hunyuan.svg';
import IdeogramSvg from './assets/Ideogram.svg';
import KlingSvg from './assets/Kling.svg';
import LtxSvg from './assets/LTX.svg';
import MidjourneySvg from './assets/Midjourney.svg';
import MurekaSvg from './assets/Mureka.svg';
import PikaSvg from './assets/PikaIcon.svg';
import PixVerseSvg from './assets/PixVerse.svg';
import QwenSvg from './assets/Qwen.svg';
import SeaweedSvg from './assets/Seaweed.svg';
import SeedEditSvg from './assets/SeedEdit.svg';
import SeedreamSvg from './assets/Seedream.svg';
import SoraSvg from './assets/Sora.svg';
import TripoSvg from './assets/Tripo.svg';
import VeoSvg from './assets/Veo.svg';
import ViduSvg from './assets/Vidu.svg';
import WanSvg from './assets/Wan.svg';
import ImagenSvg from './assets/Imagen.svg';
import NanoBananaSvg from './assets/NanoBananaIcon.svg';
import styles from './index.module.scss';

const partnerLogos = [
  { Icon: DeepSeekSvg, alt: 'DeepSeek' },
  { Icon: DoubaoSvg, alt: 'Doubao' },
  { Icon: ElevenLabsSvg, alt: 'ElevenLabs' },
  { Icon: FluxSvg, alt: 'Flux' },
  { Icon: GptImageSvg, alt: 'GPT Image' },
  { Icon: GeminiSvg, alt: 'Gemini' },
  { Icon: GrokSvg, alt: 'Grok' },
  { Icon: HunyuanSvg, alt: 'Hunyuan' },
  { Icon: IdeogramSvg, alt: 'Ideogram' },
  { Icon: KlingSvg, alt: 'Kling' },
  { Icon: LtxSvg, alt: 'LTX' },
  { Icon: MidjourneySvg, alt: 'Midjourney' },
  { Icon: MurekaSvg, alt: 'Mureka' },
  { Icon: PikaSvg, alt: 'Pika' },
  { Icon: PixVerseSvg, alt: 'PixVerse' },
  { Icon: QwenSvg, alt: 'Qwen' },
  { Icon: SeaweedSvg, alt: 'Seaweed' },
  { Icon: SeedEditSvg, alt: 'SeedEdit' },
  { Icon: SeedreamSvg, alt: 'Seedream' },
  { Icon: SoraSvg, alt: 'Sora' },
  { Icon: TripoSvg, alt: 'Tripo' },
  { Icon: VeoSvg, alt: 'Veo' },
  { Icon: ViduSvg, alt: 'Vidu' },
  { Icon: WanSvg, alt: 'Wan' },
  { Icon: ImagenSvg, alt: 'Imagen' },
  { Icon: NanoBananaSvg, alt: 'nano Banana' },
];

export default function PartnerLogos() {
  const trackRef = useRef<HTMLDivElement>(null);
  const wrapperRef = useRef<HTMLDivElement>(null);
  const [repeatCount, setRepeatCount] = useState(2);
  const [singleSetWidth, setSingleSetWidth] = useState(0);
  const officialHome = useOfficialHomeContext();
  // 官网 SSR 与客户端共用请求语言；英文兜底仅用于组件脱离官网上下文时的独立渲染。
  const titleContent = OFFICIAL_HOME_LOCALIZED_CONTENT[officialHome?.languageCode ?? 'en'].partnerLogos;

  useEffect(() => {
    const wrapper = wrapperRef.current;
    const track = trackRef.current;
    if (!wrapper || !track) {
      return () => {};
    }

    const measure = () => {
      const firstSet = track.querySelector(`.${styles.logoSet}`);
      if (firstSet) {
        const setWidth = (firstSet as HTMLElement).offsetWidth;
        setSingleSetWidth(setWidth);
      }
    };

    measure();
    const ro = new ResizeObserver(measure);
    ro.observe(track);

    return () => ro.disconnect();
  }, [repeatCount]);

  useEffect(() => {
    const wrapper = wrapperRef.current;
    if (!wrapper || singleSetWidth <= 0) {
      return () => {};
    }

    const wrapperWidth = wrapper.offsetWidth;
    // 一组已能铺满视口时只渲染 2 组即可无缝循环，否则按需多组
    const neededSets = singleSetWidth >= wrapperWidth
      ? 2
      : Math.ceil(wrapperWidth / singleSetWidth) + 1;
    setRepeatCount(Math.max(neededSets, 2));

    return () => {};
  }, [singleSetWidth]);

  const logoSets = useMemo(() => {
    const sets = [];
    for (let i = 0; i < repeatCount; i += 1) {
      sets.push(
        <div key={`set-${i}`} className={styles.logoSet}>
          {partnerLogos.map((logo) => (
            <div key={`logo-${i}-${logo.alt}`} className={styles.logoItem}>
              <logo.Icon aria-label={logo.alt} />
            </div>
          ))}
        </div>,
      );
    }
    return sets;
  }, [repeatCount]);

  return (
    <div className={styles.container}>
      <div className={styles.header}>
        <span className={styles.text}>{titleContent.prefix}</span>
        <span className={styles.gradientText}>{titleContent.highlight}</span>
      </div>
      <div ref={wrapperRef} className={styles.logosWrapper}>
        <div
          ref={trackRef}
          className={`${styles.logosTrack} ${singleSetWidth > 0 ? styles.logosTrackAnimated : ''}`}
          style={singleSetWidth > 0 ? {
            ['--scroll-width' as string]: `${singleSetWidth}px`,
            ['--scroll-duration' as string]: `${singleSetWidth / 30}s`,
          } : undefined}
        >
          {logoSets}
        </div>
        <div className={styles.fadeLeft} />
        <div className={styles.fadeRight} />
      </div>
    </div>
  );
}
